import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  BudgetGroupSearchRequestDto,
  BudgetGroupSearchResponseDto,
  MonitorConfigBulkUpsertResultDto,
  MonitorConfigDto,
  ResolvedMonitorConfigDto,
} from '@kortxio/hub-api';
import {
  BulkDeleteMonitorConfigArgs,
  BulkUpsertMonitorConfigArgs,
  EffectiveConfigArgs,
  UpsertMonitorConfigArgs,
} from 'features/admin-monitoring/config/types';
import { MonitorSummary } from 'features/admin-monitoring/monitorType';
import { reportApiError } from 'features/shared/reportApiError';
import api from 'libs/api';
import { AsyncThunkConfig } from 'store/types';

export const getMonitorDetail = createAsyncThunk<
  MonitorSummary,
  string,
  AsyncThunkConfig
>(
  'monitoringConfig/getMonitorDetail',
  async (monitorKey, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.monitor.getMonitor(monitorKey);

      return response.data;
    } catch (error) {
      return rejectWithValue(reportApiError(error, dispatch));
    }
  }
);

export const getMonitorConfigs = createAsyncThunk<
  MonitorConfigDto[],
  string,
  AsyncThunkConfig
>(
  'monitoringConfig/getConfigs',
  async (monitorKey, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.monitorConfig.getMonitorConfigs(monitorKey);

      return response.data;
    } catch (error) {
      return rejectWithValue(reportApiError(error, dispatch));
    }
  }
);

export const getMonitorEffectiveConfig = createAsyncThunk<
  ResolvedMonitorConfigDto,
  EffectiveConfigArgs,
  AsyncThunkConfig
>(
  'monitoringConfig/getEffectiveConfig',
  async ({ monitorKey, scope, entityId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.monitorConfig.getMonitorEffectiveConfig(
        monitorKey,
        scope,
        entityId
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(reportApiError(error, dispatch));
    }
  }
);

export const upsertMonitorConfig = createAsyncThunk<
  MonitorConfigDto,
  UpsertMonitorConfigArgs,
  AsyncThunkConfig
>(
  'monitoringConfig/upsert',
  async ({ monitorKey, body }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.monitorConfig.upsertMonitorConfig(
        monitorKey,
        body
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(reportApiError(error, dispatch));
    }
  }
);

export const bulkUpsertMonitorConfig = createAsyncThunk<
  MonitorConfigBulkUpsertResultDto,
  BulkUpsertMonitorConfigArgs,
  AsyncThunkConfig
>(
  'monitoringConfig/bulkUpsert',
  async ({ monitorKey, body }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.monitorConfig.bulkUpsertMonitorConfigs(
        monitorKey,
        body
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(reportApiError(error, dispatch));
    }
  }
);

export const bulkDeleteMonitorConfigs = createAsyncThunk<
  void,
  BulkDeleteMonitorConfigArgs,
  AsyncThunkConfig
>(
  'monitoringConfig/bulkDelete',
  async ({ monitorKey, body }, { dispatch, rejectWithValue }) => {
    try {
      await api.monitorConfig.bulkDeleteMonitorConfigs(monitorKey, body);
    } catch (error) {
      return rejectWithValue(reportApiError(error, dispatch));
    }
  }
);

export const getBudgetGroupSnapshot = createAsyncThunk<
  BudgetGroupSearchResponseDto,
  BudgetGroupSearchRequestDto,
  AsyncThunkConfig
>(
  'monitoringConfig/getBudgetGroupSnapshot',
  async (request, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.budgetGroup.searchBudgetGroups(request);

      return response.data;
    } catch (error) {
      return rejectWithValue(reportApiError(error, dispatch));
    }
  }
);
